<template>
    <div ref="carouselList" class="carousel-list">
        <StateEmpty v-if="isEmpty" subject="models" />
        <CarouselItem 
            v-else 
            v-for="(asset, index) in visibleAssets"
            :key="asset?.id || `asset-${index}`"
            :asset="asset"
        />
    </div>
</template>

<script>
import { mapGetters } from '@generio-ai/library-legacy/plugins/vuex'
import StateEmpty from '@/components/common/state/StateEmpty.vue'
import CarouselItem from '@/components/start/carousel/CarouselItem.vue'

export default {
    name: 'CarouselList',
    components: {
        StateEmpty,
        CarouselItem
    },
    props: {
        assets: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            maxItems: 0,
            resizeObserver: null,
            itemSize: 200,
            itemGap: 20
        }
    },
    computed: {
        ...mapGetters('assets', [
            'isSynchronized'
        ]),
        isEmpty() {
            return this.isSynchronized && !this.hasAssets
        },
        hasAssets() {
            return Array.isArray(this.assets) && this.assets.length > 0
        },
        visibleAssets() {
            if (!this.isSynchronized) return Array.from({ length: this.maxItems }, () => {})

            if (!this.hasAssets) return []
            if (this.maxItems <= 0) return []

            if (this.assets.length > this.maxItems) {
                return this.assets.slice(0, Math.max(this.maxItems, 0))
            }

            return this.assets
        }
    },
    methods: {
        updateMaxItems() {
            const list = this.$refs.carouselList
            const width = list?.clientWidth || 0

            if (width <= 0) {
                this.maxItems = 0
                return
            }

            const totalItemSize = this.itemSize + this.itemGap
            const count = Math.floor((width + this.itemGap) / totalItemSize)
            this.maxItems = Math.max(2, count)
        }
    },
    mounted() {
        this.$nextTick(this.updateMaxItems)

        if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(() => this.updateMaxItems())
            if (this.$refs.carouselList) {
                this.resizeObserver.observe(this.$refs.carouselList)
            }
        } else {
            window.addEventListener('resize', this.updateMaxItems)
        }
    },
    beforeUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect()
            this.resizeObserver = null
        } else {
            window.removeEventListener('resize', this.updateMaxItems)
        }
    }
}
</script>

<style scoped>
@keyframes animation {
    0% {
        opacity: 0;
        transform: translateY(75px);
    }
    100% {
        opacity: 1;
        transform: translateY(0);
    }
}

.carousel-list {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    animation: animation 0.5s ease-out forwards;
}

@media(max-width: 1000px) {
    .carousel-list {
        justify-content: center;
    }
}

@media(max-width: 450px) {
    .carousel-list {
        flex-direction: column;
        justify-content: flex-start;
    }
}
</style>
